import React, { useMemo, useState } from 'react';

import ObjectiveIcon from '@atlaskit/icon/core/objective';
import { Text } from '@atlaskit/primitives/compiled/text';
import { Ati } from '@atlassian/ari';
import { TownsquareSiteAri } from '@atlassian/ari/townsquare/site';
import type { MenuItem } from '@atlassian/universal-create/types';
import { useIntl } from 'react-intl';
import { graphql, useLazyLoadQuery } from 'react-relay';

import { useAnalytics } from '@townsquare/analytics';
import { DEFAULT_TOP_NAV4_ATTRS } from '@townsquare/analytics-nav4';
import { useCreateGoal } from '@townsquare/create-goal-modal';
import { useUniversalCreateStore } from '@townsquare/create-store';
import { filterUndefined } from '@townsquare/filter-type';
import { getGoalTypeNameIntl } from '@townsquare/goal-types/name';
import { GoalCreateContent } from '@townsquare/navigation/goal-create-content';
import { AggRelayEnvironmentProvider } from '@townsquare/relay-utils/agg-environment';
import { useRouter } from '@townsquare/router/useRouter';
import { useWorkspaceCloudId } from '@townsquare/workspace-store';

import { useGoalUniversalCreateItemsQuery } from './__generated__/useGoalUniversalCreateItemsQuery.graphql';

export const useGoalUniversalCreateItems = (): MenuItem[] => {
	const intl = useIntl();
	const [cloudId] = useWorkspaceCloudId();
	let containerId = '';
	try {
		containerId = TownsquareSiteAri.create({ siteId: cloudId }).toString();
	} catch (error) {
		// Do nothing when we don't have a valid container ID
		// This is expected in the no workspace experience
	}

	const analytics = useAnalytics();
	const [{ route }] = useRouter();
	const { openCreateGoal } = useCreateGoal();
	const [_, { setIsOpen }] = useUniversalCreateStore();
	const [selectedGoalTypeAri, setSelectedGoalTypeAri] = useState<string | undefined>();

	const fireAnalyticsEvent = useMemo(
		() => (itemId: string) => {
			void analytics.ui(
				'navigationItem',
				'clicked',
				{
					route: route.name,
					...DEFAULT_TOP_NAV4_ATTRS,
					itemId,
				},
				'createNavigationMenuItem',
				['townsquare', 'universal-create'],
			);
		},
		[analytics, route.name],
	);

	/**
	 * Using useLazyLoadQuery here so there is no delay in querying these goal types
	 * They need to be loaded up front so there is no delay showing them when a user
	 * opens the universal create menu
	 */
	// eslint-disable-next-line @atlassian/data/no-wrapping-hooks
	const data = useLazyLoadQuery<useGoalUniversalCreateItemsQuery>(
		graphql`
			query useGoalUniversalCreateItemsQuery($containerId: String!, $hasContainerId: Boolean!) {
				townsquare @include(if: $hasContainerId) {
					goalTypes(containerId: $containerId, first: 100, includeDisabled: true) {
						edges {
							node {
								ari
								kind
								state
								name {
									... on GoalTypeCustomName {
										value
									}
									... on LocalizationField {
										messageId
										defaultValue
									}
								}
							}
						}
					}
				}
			}
		`,
		{ containerId, hasContainerId: Boolean(containerId) },
		{ fetchPolicy: 'store-or-network' },
	);

	const goalUniversalCreateItems = useMemo(() => {
		const goalTypeEdges = data.townsquare?.goalTypes?.edges;
		return goalTypeEdges
			?.map((goalType) => {
				if (!goalType?.node) {
					return;
				}
				// Goals of kind SUCCESS_MEASURE cannot be created in isolation through universal create
				// Instead, they are created and attached directly on a parent goal
				if (goalType.node.kind === 'SUCCESS_MEASURE' || goalType.node.state !== 'ENABLED') {
					return;
				}
				const messageId = goalType?.node?.name?.messageId ?? null;
				const goalTypeName =
					goalType?.node?.name?.value ||
					getGoalTypeNameIntl({
						messageId,
						defaultValue: goalType?.node?.name?.defaultValue,
						intl,
					});

				return {
					ati: Ati.parse('ati:cloud:townsquare:goal'),
					testId: 'new-goal-button',
					children: <Text>{goalTypeName}</Text>,
					elemBefore: ObjectiveIcon,
					onClickCloseMenu: false,
					loadCreateContent: (contentProps, menuControl) => (
						<AggRelayEnvironmentProvider>
							<GoalCreateContent
								{...contentProps}
								{...menuControl}
								goalTypeId={selectedGoalTypeAri}
								closeMenu={() => {
									menuControl.close();
									setIsOpen(false);
								}}
								goalTypeName={goalTypeName}
							/>
						</AggRelayEnvironmentProvider>
					),
					onClick: () => {
						setSelectedGoalTypeAri(goalType.node?.ari);
						fireAnalyticsEvent('goal');
						openCreateGoal({
							redirectOnSave: true,
							triggeredFrom: 'townsquare-universal-create-button',
						});
					},
				} as MenuItem;
			})
			.filter(filterUndefined);
	}, [
		data.townsquare?.goalTypes?.edges,
		intl,
		selectedGoalTypeAri,
		setIsOpen,
		fireAnalyticsEvent,
		openCreateGoal,
	]);

	return goalUniversalCreateItems || [];
};
